// src/components/pages/donations/LandDetailPage.tsx
// LandDetailPage.tsx — Tailwind/DGA styling pass only. No logic changes (except guard relax to support breadcrumb/deeplink).
// ----------------------------------------------------------------------------------
// Purpose: Show a single donation land (benefaction) detail page with:
//  - back/forward gating (must arrive from the list page OR from breadcrumb OR direct deep link by :id)
//  - data fetch by :landId
//  - info panel (region/city/center/district/size/price)
//  - Google Map preview + QR code that opens the same spot in Google Maps (ar/SA)
//  - "Apply/Proceed" button that respects auth and flows through a continue-token
// ----------------------------------------------------------------------------------

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "../../../utils/i18nStub";
import { LandDetail } from "../../../types/donations";
import { getDonationLandById } from "../../../services/donationsService";
import { GoogleMapsLoader } from "../../maps/GoogleMapsLoader";
import { isAuthenticated } from "../../../services/authService";
import * as donorService from "../../../services/donorService";
import NotBenefactorModal from "../../NotBenefactorModal";

// include `source` to allow breadcrumb clicks to pass the guard
type NavState =
  | {
      fromList?: boolean;
      source?: "breadcrumb" | "list" | "direct" | string;
    }
  | null;

const fmtNum = (v?: number | null) => (v == null ? "—" : Intl.NumberFormat("en-US").format(v ?? 0));

export default function LandDetailPage() {
  const { t } = useTranslation("donations");
  const { landId } = useParams<{ landId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState) || null;

  const [data, setData] = useState<LandDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showNotBenefactorModal, setShowNotBenefactorModal] = useState(false);
  const [proceedChecking, setProceedChecking] = useState(false);
  const hasLoadedRef = useRef(false);
  const hasRedirectedRef = useRef(false);

  // ---- relaxed guard: allow from list, from breadcrumb, or valid numeric :id (deeplink) ----
  const idNum = Number(landId);
  const allowEntry =
    navState?.fromList === true ||
    navState?.source === "breadcrumb" ||
    (Number.isFinite(idNum) && idNum > 0);

  useEffect(() => {
    if (!allowEntry && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      navigate("/benefaction/lands", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowEntry]);

  // ---- data fetch: always attempt when we have a valid :id; redirect on failure ----
  useEffect(() => {
    // Prevent infinite loops - if already loaded or redirected, don't run again
    if (hasLoadedRef.current || hasRedirectedRef.current) return;
    
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const id = Number(landId);
        if (!Number.isFinite(id) || id <= 0) throw new Error("Invalid id");

        const d = await getDonationLandById(id);
        if (!alive) return;

        if (!d) {
          if (!hasRedirectedRef.current) {
            hasRedirectedRef.current = true;
            navigate("/benefaction/lands", { replace: true });
          }
          return;
        }
        setData(d ?? null);
        hasLoadedRef.current = true;
      } catch (e: any) {
        if (alive && !hasRedirectedRef.current) {
          setErr(e?.message || String(e));
          hasRedirectedRef.current = true;
          navigate("/benefaction/lands", { replace: true });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landId]);

  // Map bootstrap
  const mapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!data?.latitude || !data?.longitude) return;
    let map: google.maps.Map | null = null;
    GoogleMapsLoader.load("ar", "SA")
      .then((google) => {
        if (!mapRef.current) return;
        const center = { lat: Number(data.latitude), lng: Number(data.longitude) };
        map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 14,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });
        new google.maps.Marker({ position: center, map });
      })
      .catch(() => {});
    return () => {
      map = null;
    };
  }, [data?.latitude, data?.longitude]);

  const breadcrumb = useMemo(
    () => [
      { label: t("detail.breadcrumb.home", "الرئيسية"), to: "/" },
      { label: t("detail.breadcrumb.section", "الفرص المتاحة لبناء مسجد"), to: "/benefaction/lands" },
      { label: t("detail.pageTitle", "الأرض"), to: null },
    ],
    [t]
  );

  const handleProceed = async () => {
    const idNum = Number(landId ?? 0);

    if (!idNum || idNum <= 0) {
      console.error("[LandDetailPage] Invalid land ID:", landId);
      return;
    }

    if (!isAuthenticated()) {
      setShowNotBenefactorModal(true);
      return;
    }

    setProceedChecking(true);
    try {
      const donors = await donorService.listMine();
      if (donors && donors.length > 0) {
        navigate(`/benefaction/request/${idNum}`);
      } else {
        setShowNotBenefactorModal(true);
      }
    } catch {
      setShowNotBenefactorModal(true);
    } finally {
      setProceedChecking(false);
    }
  };

  if (loading)
    return (
      <div className="dga-container py-6">{t("detail.loading", "جاري التحميل…")}</div>
    );

  if (err || !data) {
    return (
      <div className="dga-container py-6">
        <div className="mb-4 text-red-600 dark:text-red-400">{err || t("detail.notFound", "العقار غير موجود")}</div>
        <button
          className="inline-flex items-center gap-2 rounded-2xl border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={() => navigate(-1)}
        >
          {t("detail.back", "رجوع")}
        </button>
      </div>
    );
  }

  const mapsUrl =
    data.latitude && data.longitude
      ? `https://www.google.com/maps?q=${encodeURIComponent(`${data.latitude},${data.longitude}`)}&hl=ar&z=16`
      : "";

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          {breadcrumb.map((b, i) => (
            <li key={i} className="flex items-center gap-2">
              {b.to ? (
                <Link to={b.to} className="hover:text-neutral-800 dark:hover:text-neutral-200 transition-colors">
                  {b.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-neutral-800 dark:text-neutral-100">{b.label}</span>
              )}
              {i < breadcrumb.length - 1 && <span className="opacity-60">/</span>}
            </li>
          ))}
        </ol>
      </nav>

      {/* Grid: 12 cols on lg+ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT: Info (8 cols) */}
        <section
          className="col-span-12 lg:col-span-8 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm"
          aria-labelledby="infoTitle"
        >
          <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
            <h2 id="infoTitle" className="text-sm font-semibold">{t("detail.info.title", "معلومات الأرض")}</h2>
          </div>
          <div className="p-4">
            {/* Rows: labels right, values left */}
            <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-3 items-baseline">
              <dt className="col-start-1">
                {t("detail.sequence", "الرقم التسلسلي")}
              </dt>
              <dd className="col-start-2">{data.sequence || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.region", "المنطقة")}
              </dt>
              <dd className="col-start-2">{data.regionName || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.city", "المدينة")}
              </dt>
              <dd className="col-start-2">{data.cityName || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.center", "المركز")}
              </dt>
              <dd className="col-start-2">{data.moiaCenterName || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.district", "الحي")}
              </dt>
              <dd className="col-start-2">{data.districtName || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.address", "العنوان الكامل")}
              </dt>
              <dd className="col-start-2">{data.fullAddress || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.donationType", "نوع المساهمة ")}
              </dt>
              <dd className="col-start-2">{data.donationType || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.plot", "رقم القطعة")}
              </dt>
              <dd className="col-start-2">{data.landNumber || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.north", "طول الأرض شمالًا")}
              </dt>
              <dd className="col-start-2">{data.north || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.south", "طول الأرض جنوبًا")}
              </dt>
              <dd className="col-start-2">{data.south || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.east", "طول الأرض شرقًا")}
              </dt>
              <dd className="col-start-2">{data.east || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.west", "طول الأرض غربًا")}
              </dt>
              <dd className="col-start-2">{data.west || "—"}</dd>

              <dt className="col-start-1">
                {t("detail.area", "المساحة")}
              </dt>
              <dd className="col-start-2">{fmtNum(data.buildingArea)} م²</dd>
            </dl>

            {/* Divider */}
            <div className="my-2 h-px bg-neutral-200 dark:bg-neutral-800" />

            {/* Price row */}
            <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-3 items-baseline">
              <dt className="col-start-1">
                {t("detail.price", "الملاءة المالية المطلوبة")}
              </dt>
              <dd className="col-start-2">
                <span className="inline-flex items-center gap-2">
                  <span className="text-lg font-semibold">{fmtNum(data.aproxLandPrice)}</span>
                  <img
                    className="inline-block w-3 h-3 align-[-2px]"
                    src={`${process.env.PUBLIC_URL}/dga/assets/icons/riyal-sar.svg`}
                    alt="ريال سعودي"
                    onError={(e) => {
                      e.currentTarget.replaceWith(
                        Object.assign(document.createElement("span"), {
                          textContent: "﷼",
                          ariaLabel: "ريال سعودي",
                          className: "inline-block text-xs leading-none align-[-2px]",
                        })
                      );
                    }}
                  />
                </span>
              </dd>
              {/* Bottom actions */}
            </dl>

            {/* Divider */}
            <div className="my-2 h-px bg-neutral-200 dark:bg-neutral-800" />

            {/* Bottom actions (match donation-details button sizing) */}
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                className="inline-flex h-12 items-center justify-center rounded-lg border border-neutral-300 bg-white px-4 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                onClick={() => navigate(-1)}
              >
                {t("detail.back", "رجوع")}
              </button>

              <button
                type="button"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-sa-600 px-4 text-sm font-semibold text-white hover:bg-sa-700 transition-all focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-2 whitespace-nowrap disabled:opacity-70"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleProceed();
                }}
                disabled={proceedChecking}
              >
                {proceedChecking ? "جاري التحقق..." : t("detail.applyNow", "تقديم الطلب")}
              </button>
            </div>
          </div>
        </section>

        {/* RIGHT: Map + QR (4 cols) */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <section
            className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm"
            aria-labelledby="mapTitle"
          >
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
              <h2 id="mapTitle" className="text-sm font-semibold text-center">{t("detail.map.title", "الموقع الجغرافي")}</h2>
            </div>
            <div className="p-4">
              <div ref={mapRef} className="h-72 rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-800" role="img" aria-label={t("detail.map.title")} />
            </div>
          </section>

          <section
            className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm"
            aria-labelledby="qrTitle"
          >
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
              <h2 id="qrTitle" className="text-sm font-semibold text-center">{t("detail.qr.title", "باركود")}</h2>
            </div>
            <div className="p-4 flex items-center justify-center">
              {mapsUrl ? (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" title="Open in Google Maps">
                  <img
                    alt="QR"
                    width={140}
                    height={140}
                    className="border border-neutral-200 dark:border-neutral-800 rounded"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(mapsUrl)}`}
                  />
                </a>
              ) : (
                <div className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded flex items-center justify-center w-[140px] h-[140px]">
                  <span className="text-neutral-500">QR</span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
      <NotBenefactorModal
  open={showNotBenefactorModal}
  onClose={() => setShowNotBenefactorModal(false)}
  isLoggedIn={isAuthenticated()}
/>
    </div>
  );
}
